import React from 'react'
import { useTheme } from '@mui/system'
import HorizontalBarChart from './utils/HorizontalBarChart'
import { BarSeriesType } from '@mui/x-charts'
import { EnrichedIssueEntity } from 'types/IssueEntity'
import {
    calculateIssueTotalOfferingSize,
    calculateSumSubscriptionCommitmentInPercentage,
    getCommitChartColorIndex,
    getCommitChartSegmentLabel,
    getCommitmentsInPercentage,
    getSummarizedFeeLevels,
    randomBlue,
} from '../../pages/issues/utils'
import { getCommitChartStackColors } from './commitChartPalette'
import { roundFloat } from '../../packages/utils/numberFormatters'

type SerieType = {
    label: string
    data: [number]
    color: string
}

const CommitChartWithInvestors = ({
    isLeftAlign,
    issue,
    size,
    skipAnimation,
}: {
    isLeftAlign?: boolean
    issue: Pick<
        EnrichedIssueEntity,
        | 'abbTarget'
        | 'investors'
        | 'secondaryOffering'
        | 'totalOfferedUnits'
        | 'approxTotalCommitment'
        | 'fees'
        | 'unitSubPrice'
    >
    size: 'small' | 'large'
    skipAnimation?: boolean
}) => {
    const theme = useTheme()

    const chartColors = getCommitChartStackColors(theme)
    const investorsExcludingSellers = issue.investors.filter((investor) => !investor.isSeller)
    const maxFeeLevel = Math.max(0, ...investorsExcludingSellers.map((investor) => investor.feeLevel ?? 0))
    const secondaryOffering = issue.secondaryOffering !== null ? issue.secondaryOffering : 0
    const { totalOfferingSize } = calculateIssueTotalOfferingSize(issue)
    const summarizedFeeLevels = getSummarizedFeeLevels(investorsExcludingSellers, totalOfferingSize)

    const totalCommitment = Math.min(
        summarizedFeeLevels.reduce(
            (acc, commitment) => (commitment.amountInPercentage > 0 ? acc + (commitment.amountInPercentage || 0) : acc),
            0,
        ),
        100,
    )

    const sumSubscriptionCommitmentInPercentage = calculateSumSubscriptionCommitmentInPercentage({
        investors: investorsExcludingSellers,
        totalOfferingSize,
    })

    const sumGarantCommitmentInPercentage = investorsExcludingSellers.reduce(
        (acc, investor) => (investor.feeLevel > 0 ? acc + (investor.amountInPercentage || 0) : acc),
        0,
    )
    const sumGarantCommitmentInCash = investorsExcludingSellers.reduce(
        (acc, investor) => (investor.feeLevel > 0 ? acc + (investor.amountInCash || 0) : acc),
        0,
    )

    const totalSubscriptionCommitmentInPercentage = sumSubscriptionCommitmentInPercentage

    const totalOffered = (issue.totalOfferedUnits || 0) + secondaryOffering

    const totalGarantCommitmentInPercentage =
        sumGarantCommitmentInPercentage > 0
            ? sumGarantCommitmentInPercentage
            : (sumGarantCommitmentInCash * 100) / (totalOffered || 1)

    let notGuaranteed =
        100 -
        totalGarantCommitmentInPercentage -
        totalSubscriptionCommitmentInPercentage -
        (issue.approxTotalCommitment ? +issue.approxTotalCommitment : 0)

    const totalTaCommitmentPercentage = summarizedFeeLevels
        .filter((row) => row.feeLevel === 0)
        .reduce((sum, row) => sum + row.amountInPercentage, 0)

    const seenMiddleDownForFeeLevel = new Set<number>()

    const series = summarizedFeeLevels.reduce((acc: SerieType[], item) => {
        const { feeLevel, amountInPercentage } = item
        const feeLevelObject = issue.fees?.find((fee) => fee.feeLevel === feeLevel)
        const colorIdx = getCommitChartColorIndex(item)
        const color = chartColors[colorIdx] ?? randomBlue()

        if (feeLevelObject?.middleDown) {
            if (!seenMiddleDownForFeeLevel.has(feeLevel)) {
                seenMiddleDownForFeeLevel.add(feeLevel)
                const notAssured = feeLevelObject.middleDown - totalTaCommitmentPercentage
                if (notAssured > 0) {
                    notGuaranteed -= notAssured
                    acc.push({
                        label: 'Ej säkerställt',
                        data: [roundFloat(notAssured)],
                        color: theme.palette.grey[200],
                    })
                }
            }
            acc.push({
                label: getCommitChartSegmentLabel(item, maxFeeLevel),
                data: [roundFloat(amountInPercentage)],
                color,
            })
            return acc
        }

        if (feeLevelObject?.topDown && notGuaranteed > 0) {
            acc.push({
                label: 'Ej säkerställt',
                data: [roundFloat(notGuaranteed)],
                color: theme.palette.grey[200],
            })
            notGuaranteed = 0
        }

        acc.push({
            label: getCommitChartSegmentLabel(item, maxFeeLevel),
            data: [roundFloat(amountInPercentage)],
            color,
        })

        return acc
    }, [])

    if (notGuaranteed > 0) {
        series.push({
            label: 'Ej säkerställt',
            data: [roundFloat(notGuaranteed)],
            color: theme.palette.grey[200],
        })
        notGuaranteed = 0
    }

    return (
        <HorizontalBarChart
            isLeftAlign={isLeftAlign}
            labelValue={totalCommitment || 0}
            series={series}
            size={size}
            skipAnimation={skipAnimation}
        />
    )
}

export function CommitChart({
    isLeftAlign,
    issue,
    size,
    skipAnimation,
}: {
    isLeftAlign?: boolean
    issue: Pick<
        EnrichedIssueEntity,
        | 'approxCommitment'
        | 'approxGuarantor'
        | 'noInvestorsNamed'
        | 'abbTarget'
        | 'investors'
        | 'secondaryOffering'
        | 'totalOfferedUnits'
        | 'approxTotalCommitment'
        | 'fees'
        | 'unitSubPrice'
    >
    size: 'small' | 'large'
    skipAnimation?: boolean
}) {
    const theme = useTheme()

    let series: (Omit<BarSeriesType, 'type'> & { label: string; data: [number] })[] | null = null

    if (issue.id === '749f4cf0-9062-4430-b7ca-fdb048e8b22e') {
        console.log('issue: ', issue)
    }

    if (issue.investors.length > 0) {
        return (
            <CommitChartWithInvestors
                isLeftAlign={isLeftAlign}
                issue={issue}
                size={size}
                skipAnimation={skipAnimation}
            />
        )
    }

    const commitments = getCommitmentsInPercentage(issue)

    if (!commitments) return null

    let { commitment, guarantor, totalCommitment, notAssured } = commitments

    if (commitment) {
        if (totalCommitment > 100) {
            // Normalize to 100%, so that the chart does not collapse
            commitment = (100 * commitment) / totalCommitment
            guarantor = (100 * (guarantor || 0)) / totalCommitment
            totalCommitment = 100
        }

        series = [
            {
                label: 'Teckningsåtagande',
                data: [roundFloat(commitment, 4)],
                color: theme.palette.info.main,
            },
            {
                label: 'Garantiåtagande',
                data: [guarantor ? roundFloat(guarantor, 4) : 0],
                color: theme.palette.info.light,
            },
            {
                label: 'Ej säkerställt',
                data: [roundFloat(notAssured, 4)],
                color: theme.palette.grey[200],
            },
        ]
    } else {
        series = [
            {
                label: 'Totalt åtagande',
                data: [roundFloat(totalCommitment, 4)],
                color: theme.palette.info.main,
            },
            {
                label: 'Ej säkrat',
                data: [roundFloat(notAssured, 4)],
                color: theme.palette.grey[200],
            },
        ]
    }

    return (
        <HorizontalBarChart
            isLeftAlign={isLeftAlign}
            labelValue={commitments.totalCommitment || 0}
            series={series}
            size={size}
            skipAnimation={skipAnimation}
        />
    )
}
